import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 40, right: 160, bottom: 40, left: 100 };
const RANK_ROW_HEIGHT = 30;
const SVG_WIDTH = 700;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function StateRankingTimeline({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [highlightedState, setHighlightedState] = useState(null);

  const maxRank = useMemo(() => {
    if (!data || !data.states) return 0;
    let max = 0;
    data.states.forEach((s) => {
      s.ranks.forEach((r) => {
        if (r != null && r > max) max = r;
      });
    });
    return max;
  }, [data]);

  const svgHeight = useMemo(() => {
    if (maxRank === 0) return 400;
    return Math.max(400, maxRank * RANK_ROW_HEIGHT + MARGIN.top + MARGIN.bottom);
  }, [maxRank]);

  useEffect(() => {
    if (!data || !data.dates || !data.states || data.dates.length === 0 || data.states.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = SVG_WIDTH - MARGIN.left - MARGIN.right;
    const innerH = svgHeight - MARGIN.top - MARGIN.bottom;

    svg.attr('viewBox', `0 0 ${SVG_WIDTH} ${svgHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const x = d3.scalePoint().domain(data.dates).range([0, innerW]).padding(0.1);
    const y = d3.scaleLinear().domain([1, maxRank]).range([0, innerH]);

    // Grid lines
    for (let rank = 1; rank <= maxRank; rank++) {
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', y(rank))
        .attr('y2', y(rank))
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);
    }

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH + 8})`)
      .call(d3.axisBottom(x).tickSize(0).tickPadding(8))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    const yAxis = g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(Math.min(maxRank, 20))
          .tickFormat((d) => Number.isInteger(d) ? `#${d}` : '')
      );

    yAxis.selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    yAxis.select('.domain').attr('stroke', '#e5e7eb');
    yAxis.selectAll('.tick line').attr('stroke', '#f3f4f6');

    const line = d3.line()
      .x((d, i) => x(data.dates[i]))
      .y((d) => y(d))
      .curve(d3.curveBumpX)
      .defined((d) => d != null);

    // Draw state groups
    const stateGroups = g.selectAll('.state-group')
      .data(data.states)
      .enter()
      .append('g')
      .attr('class', 'state-group');

    stateGroups.each(function (stateData) {
      const group = d3.select(this);
      const color = stateData.color || '#6b7280';

      // Line path
      group.append('path')
        .datum(stateData.ranks)
        .attr('d', line)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2.5)
        .attr('opacity', 0.85)
        .attr('class', 'state-line');

      // Circles at each data point
      stateData.ranks.forEach((rank, i) => {
        if (rank == null) return;
        group.append('circle')
          .attr('cx', x(data.dates[i]))
          .attr('cy', y(rank))
          .attr('r', 5)
          .attr('fill', color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1.5)
          .attr('class', 'state-dot');
      });

      // Left label at initial rank
      const firstRank = stateData.ranks.find((r) => r != null);
      if (firstRank != null) {
        group.append('text')
          .attr('x', -10)
          .attr('y', y(firstRank))
          .attr('dy', '0.35em')
          .attr('text-anchor', 'end')
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', color)
          .text(stateData.state);
      }

      // Right label at final rank
      const lastValidRanks = stateData.ranks.filter((r) => r != null);
      const lastRank = lastValidRanks.length > 0 ? lastValidRanks[lastValidRanks.length - 1] : null;
      if (lastRank != null) {
        group.append('text')
          .attr('x', innerW + 10)
          .attr('y', y(lastRank))
          .attr('dy', '0.35em')
          .attr('text-anchor', 'start')
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', color)
          .text(stateData.state);

        // Rank change badge
        const change = stateData.rankChange;
        if (change != null && change !== 0) {
          const badgeX = innerW + 42;
          const badgeY = y(lastRank);
          // Positive rankChange = moved up in rank (became more expensive) = green up arrow
          // Negative rankChange = moved down in rank (became cheaper) = red down arrow
          const isUp = change > 0;
          const badgeColor = isUp ? '#16a34a' : '#dc2626';
          const arrow = isUp ? '\u2191' : '\u2193';

          group.append('text')
            .attr('x', badgeX)
            .attr('y', badgeY)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'start')
            .attr('font-size', '10px')
            .attr('font-weight', 600)
            .attr('fill', badgeColor)
            .text(`${arrow}${Math.abs(change)}`);
        }
      }

      // Invisible wide hover zone for easier interaction
      group.append('path')
        .datum(stateData.ranks)
        .attr('d', line)
        .attr('fill', 'none')
        .attr('stroke', 'transparent')
        .attr('stroke-width', 14)
        .style('cursor', 'pointer')
        .on('mouseenter', () => {
          setHighlightedState(stateData.state);
          g.selectAll('.state-group').attr('opacity', 0.15);
          group.attr('opacity', 1);
          group.select('.state-line').attr('stroke-width', 4);
          group.selectAll('.state-dot').attr('r', 6);
        })
        .on('mouseleave', () => {
          setHighlightedState(null);
          g.selectAll('.state-group').attr('opacity', 1);
          group.select('.state-line').attr('stroke-width', 2.5);
          group.selectAll('.state-dot').attr('r', 5);
          setTooltip(null);
        })
        .on('mousemove', (event) => {
          // Find the closest date based on mouse position
          const [mx] = d3.pointer(event, g.node());
          let closestIdx = 0;
          let closestDist = Infinity;
          data.dates.forEach((date, i) => {
            const dist = Math.abs(x(date) - mx);
            if (dist < closestDist) {
              closestDist = dist;
              closestIdx = i;
            }
          });

          const rank = stateData.ranks[closestIdx];
          if (rank != null) {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              state: stateData.state,
              date: data.dates[closestIdx],
              rank,
              currentRank: stateData.currentRank,
              rankChange: stateData.rankChange,
            });
          }
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, maxRank, svgHeight, highlightedState]);

  if (!data || !data.dates || !data.states || data.dates.length === 0 || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State Price Rankings Over Time</div>
        <div style={styles.subtitle}>
          How state average pricing ranks change across scrape dates (1 = most expensive)
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>State Price Rankings Over Time</div>
      <div style={styles.subtitle}>
        How state average pricing ranks change across scrape dates (1 = most expensive)
      </div>
      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Date</span>
            <span style={{ fontWeight: 600 }}>{tooltip.date}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Rank</span>
            <span style={{ fontWeight: 600, color: '#2563eb' }}>#{tooltip.rank}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Current Rank</span>
            <span style={{ fontWeight: 600 }}>#{tooltip.currentRank}</span>
          </div>
          {tooltip.rankChange != null && tooltip.rankChange !== 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Change</span>
              <span style={{
                fontWeight: 600,
                color: tooltip.rankChange > 0 ? '#16a34a' : '#dc2626',
              }}>
                {tooltip.rankChange > 0 ? '\u2191' : '\u2193'}{Math.abs(tooltip.rankChange)} rank{Math.abs(tooltip.rankChange) !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
